import React, { useState, useContext } from 'react';
import { Box, Typography } from '@material-ui/core';
import { CheckboxInput, MapperList } from '../FormElements';
import { NodeMapDefinition } from '@terragotech/gen5-datamapping-lib';
import { V2TextTemplateComponent, V2NumberTemplateComponent } from '@terragotech/page-renderer';
import { JSONSchema6 } from 'json-schema';
import { isEqual } from 'lodash';
import { LocalSchemaDefinition } from '../../utils/useSchemaLookup';
import { usePageSchemas } from './hooks/usePageSchemas';
import { PageContext } from './contexts/PageContext';
import { checkDuplicatePageName } from '../../pages/aggregates/utils/pageUtils';
import usePageRedirect from './hooks/usePageRedirect';
import { TextInputTG as TextInput } from 'views/components/formElements';
import { useCustomPageStyles } from './commonStyles';
import ModalFrame from './ModalFrame';
import MapperItem from 'components/FormDialog/MapperItem';
import { useConfirmDialog } from 'context/ConfirmContext';
import { CONFIRMATION, expandPath, getPresentValue } from 'utils/Utils';

export type TextTemplateWithName =
  | (V2TextTemplateComponent & {
      name: string;
      droppableId: string;
    })
  | (V2NumberTemplateComponent & {
      name: string;
      droppableId: string;
    });

interface TextInputEditFormProps {
  onSubmit: (result: TextTemplateWithName) => void;
  onClose: () => void;
  component: TextTemplateWithName;
}

const mapComponentTypeToOutputType = (componentType: string) => {
  switch (componentType) {
    case 'number':
    case 'time':
    case 'date':
    case 'datetime':
      return 'Number';
    default:
      return 'String';
  }
};

export const TextInputEditForm: React.FC<TextInputEditFormProps> = ({
  onSubmit,
  onClose,
  component,
}) => {
  const [name, setName] = useState(component.name);
  const [label, setLabel] = useState(component.label);
  const [repeats, setRepeats] = useState(component.repeats);
  const [oneLine, setOneLine] = useState(component.oneLine);
  const [errorMap, setErrorMap] = useState<NodeMapDefinition[] | undefined>(
    component.errorMap || undefined
  );
  const [warningMap, setWarningMap] = useState<NodeMapDefinition[] | undefined>(
    component.warningMap || undefined
  );
  const [displayOptions, setDisplayOptions] = useState(component.displayOptions || undefined);
  const [conditionalOpen, setConditionalOpen] = useState(false);
  const classes = useCustomPageStyles();
  const pageSchemas = usePageSchemas();
  const { isForceLeaveConfirmed } = usePageRedirect();
  const [existingNameError, setExistingNameError] = useState(false);
  const { pageDefinition } = useContext(PageContext);
  const { openConfirmation } = useConfirmDialog();

  const nameHelperTxt =
    'Duplicate name error. The name must be unique accross all workflow elements';

  const parts = (component.droppableId || '').split('.');
  const t =
    !component.droppableId || component.droppableId === 'page'
      ? []
      : expandPath(parts, pageSchemas.FORM.schema.properties as JSONSchema6);

  let pageValue = { value: { type: mapComponentTypeToOutputType(component.type).toLowerCase() } };
  for (let i = 0; i < t.length; i++) {
    pageValue = { ...pageValue, ...t[i] };
  }

  const errorWarningSchemas: LocalSchemaDefinition = {
    FORMVALUE: {
      schema: {
        type: 'object',
        properties: pageValue,
      } as JSONSchema6,
      schemaLabel: 'Current Field Value',
    },
    ...pageSchemas,
  };
  const handleClearDisplayMapper = async () => {
    const status = await openConfirmation(CONFIRMATION.commonClear);
    if (status === 'confirm') {
      setDisplayOptions(undefined);
    }
  };

  const getValue = (value: unknown, key: string) => getPresentValue(component, value, key);

  const getFormValues = () => ({
    type: component.type,
    name,
    label,
    ...getValue(repeats, 'repeats'),
    ...getValue(oneLine, 'oneLine'),
    ...(displayOptions && { displayOptions }),
    ...(errorMap && { errorMap }),
    ...(warningMap && { warningMap }),
    droppableId: component.droppableId,
  });

  const isFormDirty = () => !isEqual(component, getFormValues());

  const handleClose = async () =>
    (!isFormDirty() || (isFormDirty() && (await isForceLeaveConfirmed({ handleSubmit })))) &&
    onClose();

  const handleSubmit = async () => {
    if (checkDuplicatePageName(name, component, pageDefinition.elements)) {
      setExistingNameError(true);
    } else onSubmit(getFormValues());
  };

  return (
    <ModalFrame
      {...{ name: component.name, type: component.type, classes, handleClose, handleSubmit }}
    >
      <TextInput
        autoFocus
        id="Name"
        label="Name"
        error={existingNameError}
        helperText={existingNameError ? nameHelperTxt : ''}
        value={name}
        onChange={(value) => setName(value || '')}
        style={styles.textInput}
        fullWidth={false}
        labelRoot={classes.Root}
      />
      <TextInput
        id="Label"
        label="Label"
        value={label}
        onChange={(value) => setLabel(value || '')}
        fullWidth={false}
        style={styles.textInput}
        labelRoot={classes.Root}
      />
      <Typography className={classes.text}>Additional Options</Typography>
      <CheckboxInput title="One Line" checked={oneLine} onChange={(value) => setOneLine(value)} />
      {/* Disabling for now, until Repeatable rendering is implemented. */}
      <Box style={{ position: 'relative', bottom: 12 }}>
        <CheckboxInput
          title="Repeatable"
          checked={repeats}
          onChange={(value) => setRepeats(value)}
        />
      </Box>
      <MapperItem
        {...{
          onToggleMapper: setConditionalOpen,
          isActive: displayOptions !== undefined,
          clearMapper: handleClearDisplayMapper,
          openDataMap: conditionalOpen,
          dataMap: displayOptions,
          setDataMap: setDisplayOptions,
          localSchemaDefinition: errorWarningSchemas,
          title: 'Display Options',
          mapScenario: 'DISPLAY_OPTIONS',
          containerStyle: classes.mapperContainer,
        }}
      />
      {false && ( //Disabled for now
        <MapperList
          title="Error Mapping"
          data={errorMap || []}
          localSchemas={errorWarningSchemas}
          setData={(data) => setErrorMap(data)}
          mapScenario={'FIELD_LEVEL_ERROR'}
        />
      )}
      {false && ( //Disabled for now
        <MapperList
          title="Warning Mapping"
          data={warningMap || []}
          localSchemas={errorWarningSchemas}
          setData={(data) => setWarningMap(data)}
          mapScenario={'FIELD_LEVEL_WARNING'}
        />
      )}
    </ModalFrame>
  );
};

const styles = {
  textInput: {
    margin: '10px 0',
    width: '100%',
  },
};
